/**
 * @quotech/ui - Theme Hook
 * 
 * Universal theme management for all Quotech modules.
 * Uses localStorage key 'theme' for consistency.
 */

import { useState, useEffect, useCallback } from 'react'

export type Theme = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'theme'

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light'
  const saved = localStorage.getItem(STORAGE_KEY)
  if (saved === 'dark' || saved === 'light' || saved === 'system') {
    return saved
  }
  return 'light'
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  return theme === 'system' ? getSystemTheme() : theme
}

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return
  const isDark = resolveTheme(theme) === 'dark'
  const root = document.documentElement
  root.classList.toggle('dark', isDark)
  // Keep the legacy SCSS theme classes in lockstep with the canonical
  // `.dark` class. The master SPA's _theme-variables.scss targets
  // `.dark-theme` / `.light-theme` (e.g. `.dark-theme main { background:
  // #0b1337 }`). A same-tab header toggle only flips `.dark`; the master
  // theme-provider that owns these legacy classes never hears about it
  // (it syncs via cross-tab `storage` events only). That left a stale
  // `.dark-theme` on <html> after switching to light mode, painting
  // <main> navy under an otherwise-light page.
  root.classList.toggle('light', !isDark)
  root.classList.toggle('dark-theme', isDark)
  root.classList.toggle('light-theme', !isDark)
}

export interface UseThemeReturn {
  /** Current theme setting ('light', 'dark', or 'system') */
  theme: Theme
  /** Effective theme after resolving 'system' */
  resolvedTheme: 'light' | 'dark'
  /** Set the theme */
  setTheme: (theme: Theme) => void
  /** Toggle between light and dark (ignores system) */
  toggle: () => void
  /** Whether dark mode is currently active */
  isDark: boolean
}

export function useTheme(): UseThemeReturn {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme)
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => resolveTheme(getInitialTheme()))

  // Apply the resolved theme (add/remove the `dark` class), persist the
  // selection, and keep resolvedTheme in sync whenever the choice changes.
  useEffect(() => {
    applyTheme(theme)
    setResolvedTheme(resolveTheme(theme))
    localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  // When following the OS ('system'), react live to OS appearance changes.
  useEffect(() => {
    if (theme !== 'system' || typeof window === 'undefined') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      applyTheme('system')
      setResolvedTheme(getSystemTheme())
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [theme])

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme)
  }, [])

  // Toggle between light and dark, resolving 'system' to its current value.
  const toggle = useCallback(() => {
    setThemeState((prev) => (resolveTheme(prev) === 'dark' ? 'light' : 'dark'))
  }, [])

  return {
    theme,
    resolvedTheme,
    setTheme,
    toggle,
    isDark: resolvedTheme === 'dark',
  }
}

export default useTheme

